/* eslint-disable no-unused-vars */
import React, { useEffect, useState } from 'react';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import { Box, createTheme, FormHelperText, ThemeProvider, Typography } from '@mui/material';
import { capitalizeFirstLetter } from 'src/Utils/Function';
import CircularProgress from '@mui/material/CircularProgress';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Subject } from '../../Objects/models/Subject';
import useTailwindBrand from '../../Theme/useTailwindBrand';
import SubjectRepository from '../../Repositories/SubjectRepository';
import { getSubject } from '../../Redux/Actions/PunctualCourseActions';
import { store } from '../../Redux/store';
import { ENV_DECLARONS, getActiveBrandConfig, isBrandActive } from '../../config/environment.config';


interface FormSubjectSelectProps {
  customClass?: string;
  placeholder?: string;
  label?: string;
  error?: string;
  required?: boolean;
  readOnly?: boolean;
  callBack: (selectedSubjects: Subject[]) => void;
  defaultValue?: Subject[];
  isEditing?: boolean;
  multiple?: boolean;
}

export default function FormSubjectSelect(props: FormSubjectSelectProps) {
  const [open, setOpen] = useState(false);
  const [selects, setSelects] = useState<Subject[]>(props.defaultValue ?? []);
  const [options, setOptions] = useState<Subject[]>([]);
  const [hasFetched, setHasFetched] = useState(false);
  const { classes, brandConfig } = useTailwindBrand();
  const navigate = useNavigate();
  const loading = open && !hasFetched;
  const isMultiple = props.multiple !== false;
  const prestationDisplay = (s?: string) => isBrandActive(ENV_DECLARONS) ? `prestation${s}` : `matière${s}`

  const noOptionsFromList = (
    <Box sx={{ py: 0.5 }}>
      <Typography variant="body2" sx={{ fontFamily: brandConfig.fontFamily, color: 'text.secondary', mb: 0.5 }}>
        {options.length === 0 ? `Aucune ${prestationDisplay('')} disponible.` : `Cette ${prestationDisplay('')} ne fait pas partie de votre liste.`}

      </Typography>
      <Typography variant="body2" sx={{ fontFamily: brandConfig.fontFamily }}>
        Pour l'ajouter,{' '}
        <span
          onClick={(e) => {
            e.stopPropagation();
            navigate(`/${getActiveBrandConfig()}/tableau-de-bord/parametre`, { state: { step: 1, prestation: true } });
          }}
          style={{ color: brandConfig.colors.primary, cursor: 'pointer', fontWeight: 700, textDecoration: 'underline' }}
        >
          complétez votre profil →
        </span>
      </Typography>
    </Box>
  );
  const subjectGivenByTeachr = useSelector((state: any) => state?.punctualCourse?.subject)

  const teachr = useSelector((state: any) => state?.user?.currentUser?.teachr);
  useEffect(() => {
    store.dispatch(getSubject(teachr?.id))
  }, [teachr?.id]);

  useEffect(() => {
    let active = true;

    if (!open || hasFetched) {
      return undefined;
    }

    (async () => {
      const response = await SubjectRepository.getSubjects()
      if (active) {
        if (!isMultiple) {
          setOptions(subjectGivenByTeachr ?? []);
        } else {
          setOptions(response.members);
        }
        setHasFetched(true);
      }
    })();

    return () => {
      active = false;
    };
  }, [isMultiple, open, hasFetched, subjectGivenByTeachr]);

  React.useEffect(() => {
    if (!open) {
      setOptions([]);
      setHasFetched(false);
    }
  }, [open]);

  React.useEffect(() => {
    if (props.defaultValue != null) {
      setSelects(props.defaultValue);
    }
  }, [props.defaultValue]);

  const renderInput = (params: any) => (
    <TextField
      label={<span className={`${classes.fontFamily} ${props.error ? classes.error : classes.text} font-semibold`}>{props.label ?? capitalizeFirstLetter(prestationDisplay("s"))}</span>}
      variant="filled" {...params}
      className={`${classes.fontFamily} ${props.error ? classes.errorBorder + ' border-l-4' : props.isEditing ? 'border-blue-700' : 'border-gray-300'} /bg-white rounded-md border `}
      placeholder={selects.length === 0 ? props.placeholder : ''}
      sx={{
        backgroundColor: ('white'),
        border: "1px solid ",
        '&.MuiTextField-root .MuiFilledInput-root': {
          fontFamily: brandConfig.fontFamily,
          background: 'transparent'
        },
        '&.MuiTextField-root label': {
          fontFamily: brandConfig.fontFamily,
        },
        '&.MuiTextField-root input': {
          fontFamily: brandConfig.fontFamily,
        },
      }}
      InputProps={{
        ...params.InputProps,
        endAdornment: (
          <React.Fragment>
            {loading ? <CircularProgress className='mr-2' size={20} color="inherit" /> : null}
            {params.InputProps.endAdornment}
          </React.Fragment>
        ),
      }}
    />
  );

  const theme = createTheme({
    components: {
      MuiAutocomplete: {
        styleOverrides: {
          option: { fontFamily: brandConfig.fontFamily },
        },
      },
    },
  });

  return (
    <ThemeProvider theme={theme}>
      {isMultiple ? (
        <Autocomplete<Subject, true>
          id="subjects-select"
          fullWidth
          multiple
          value={selects}
          noOptionsText={`Cette ${prestationDisplay()} n'est pas encore disponible `}
          open={open}
          sx={{
            '&  .MuiAutocomplete-option': {
              border: "2px red solid",
            }
          }}
          onOpen={() => setOpen(true)}
          onClose={() => setOpen(false)}
          limitTags={window.screen.width > 768 ? 4 : 1}
          getLimitTagsText={(more) => more > 1 ? ` +${more} ${prestationDisplay('s')}` : ` +${more} ${prestationDisplay()}`}
          onChange={(_e, value) => { setSelects(value); props.callBack(value); }}
          disablePortal={true}
          isOptionEqualToValue={(option, value) => option.fr_FR === value.fr_FR}
          getOptionLabel={(option) => option.fr_FR}
          options={options}
          loading={loading}
          loadingText={<span className={classes.fontFamily}>Chargement ...</span>}
          popupIcon=''
          readOnly={props.readOnly ?? false}
          renderTags={(value) =>
            value.map((option, index) => (
              <span className={classes.fontFamily} key={index}>{option.fr_FR}<span className={`${!open && index === value.length - 1 && 'hidden'}`}>,&nbsp;</span></span>
            ))
          }
          renderInput={renderInput}
        />
      ) : (
        <Autocomplete<Subject>
          id="subjects-select"
          fullWidth
          value={selects[0] ?? null}
          noOptionsText={noOptionsFromList}
          open={open}
          onOpen={() => setOpen(true)}
          onClose={() => setOpen(false)}
          onChange={(_e, value) => {
            const arr = value ? [value] : [];
            setSelects(arr);
            props.callBack(arr);
          }}
          disablePortal={true}
          isOptionEqualToValue={(option, value) => option.fr_FR === value.fr_FR}
          getOptionLabel={(option) => option.fr_FR}
          options={options}
          loading={loading}
          loadingText="chargement ..."
          popupIcon=''
          readOnly={props.readOnly ?? false}
          renderInput={renderInput}
        />
      )}
      <FormHelperText className={`${classes.fontFamily} ${classes.error} italic font-semibold`}>{props.error}</FormHelperText>
    </ThemeProvider>
  );
}
